import { SubstepType } from "~/Configurators/Linear/dtos";
import { isNonNull, toMapFn } from "~/utils/reducers";
import {
  type SummaryDataProvider,
  type RailKmatSummaryData,
  type SummaryData,
  type CarriageKmatSummaryData,
} from "~/Configurators/Shaft/SummaryDataProvider";
import { removeMultipleTags } from "~/utils/domUtils";
import * as UnitCstics from "~/Configurators/Linear/v2/configuration/kllt/KLLTUnitCstics";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { getSubKmatCstics } from "~/Configurators/common/v2/helper";
import { InputCstics } from "~/Configurators/Linear/components/Substeps/GuidewaySetStep/constants";
import { formatCsticValueAsNumber } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import type { LinearCstic } from "~/Configurators/Linear/models";
import { CsticNames } from "~/Configurators/Linear/components/Substeps/CarriageDetailsSubstep/constants";
import type { WithTranslation } from "react-i18next";
import { KmatType } from "~/Configurators/Linear/kmatType";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [
    SubstepType.Coating,
    SubstepType.Preload,
    SubstepType.Accuracy,
    SubstepType.SystemCoating,
    SubstepType.SimpleLubrication,
  ];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.Attachment, SubstepType.Closure];
  const t = summaryProvider.t;

  const summaryData = prepareSubStepsInfo(summaryProvider, substeps) ?? [];

  const railAmount = summaryProvider.kmats[KmatType.SubRail]?.length ?? 1;

  [
    UnitCstics.RailLocatingFaceSegment1,
    UnitCstics.CarriageLocatingFaceSegment1,
    railAmount == 2 && UnitCstics.RailLocatingFaceSegment2,
    railAmount == 2 && UnitCstics.CarriageLocatingFaceSegment2,
  ]
    .filter(isNonNull)
    .map((name) => {
      const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
      return cstics[name];
    })
    .filter((cstic) => cstic?.value)
    .forEach((cstic) => {
      summaryData.push({
        name: t(cstic.label),
        isStandard: false,
        value: t(cstic.values.find((v) => v.value === cstic.value)?.label ?? ""),
      });
    });

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });
  return summaryData;
}

type CsticValueGetter = (cstic: LinearCstic) => string;

const makeNumberGetter =
  (unit?: string): CsticValueGetter =>
  (cstic) =>
    formatNumberValue(cstic.value, unit);
const makeLabelGetter =
  (t: WithTranslation["t"]): CsticValueGetter =>
  (cstic) =>
    t(cstic.values.find((v) => v.value === cstic.value)?.label ?? "");

export function getStep2SummaryDataRail(summaryProvider: SummaryDataProvider): RailKmatSummaryData[] | null {
  const guidewaySetDetailsStep = summaryProvider.substeps[SubstepType.KLLTGuidewaySet];
  if (!guidewaySetDetailsStep) {
    return null;
  }

  const t = summaryProvider.t;

  const summaryData: RailKmatSummaryData[] = [];

  const inputCsticNames = [
    InputCstics.TotalLength,
    InputCstics.aL,
    InputCstics.aR,
    InputCstics.partSizingType,
    InputCstics.partAmount,
  ];

  const inputCsticNamesFormatting: Record<string, (cstic: LinearCstic) => string> = {
    [InputCstics.TotalLength]: makeNumberGetter("mm"),
    [InputCstics.aL]: makeNumberGetter("mm"),
    [InputCstics.aR]: makeNumberGetter("mm"),
    [InputCstics.partSizingType]: makeLabelGetter(t),
    [InputCstics.partAmount]: makeLabelGetter(t),
  };

  inputCsticNames.forEach((inputCsticName) => {
    const subkmats = summaryProvider.kmats[guidewaySetDetailsStep.kmatType];
    const mainDef = getSubKmatCstics(guidewaySetDetailsStep, 0).input[inputCsticName];
    const getValue = inputCsticNamesFormatting[inputCsticName];
    const firstSubkmat = subkmats[0];
    const nameKey = firstSubkmat.cstics[mainDef.name].label;

    const entry: RailKmatSummaryData = {
      name: t(nameKey),
      rail: [],
    };
    subkmats.forEach((subkmat, index) => {
      const def = getSubKmatCstics(guidewaySetDetailsStep, index).input[inputCsticName];
      const cstic = subkmat.cstics[def.name];
      entry.rail.push({
        isStandard: getIsStandard(cstic),
        value: getValue(cstic),
      });
    });
    summaryData.push(entry);
  });

  summaryData.forEach((data) => {
    data.name = removeMultipleTags(data.name, "p", "span")!;
    data.rail.forEach((entry) => {
      entry.value = removeMultipleTags(entry.value, "p", "span")!;
    });
  });

  return summaryData;
}

export function getStep3SummaryDataCarriage(summaryProvider: SummaryDataProvider): CarriageKmatSummaryData[] | null {
  const carriageDetailsStep = summaryProvider.substeps[SubstepType.KLLTCarriageDetails];
  if (!carriageDetailsStep) {
    return null;
  }

  const railAmount = summaryProvider.kmats[KmatType.SubRail].length;
  const carriageAmount = summaryProvider.kmats[KmatType.SubCarriage].length;
  const carriagesPerRail = carriageAmount / railAmount;

  const t = summaryProvider.t;

  const summaryData: CarriageKmatSummaryData[] = [];

  const inputCsticNames = [
    CsticNames.CarriageDesign,
    CsticNames.KITLeft,
    CsticNames.KITRight,
    CsticNames.KITCenter,
    CsticNames.LubricationConnectorOrientation,
    // CsticNames.LubricationConnectorPosition,
    CsticNames.LubricationConnector,
    // CsticNames.LubricationConnectorAlignment,
  ] as const;

  const inputCsticNamesFormatting: Record<(typeof inputCsticNames)[number], (cstic: LinearCstic) => string> = {
    [CsticNames.CarriageDesign]: makeLabelGetter(t),
    [CsticNames.KITLeft]: makeLabelGetter(t),
    [CsticNames.KITRight]: makeLabelGetter(t),
    [CsticNames.KITCenter]: makeLabelGetter(t),
    [CsticNames.LubricationConnectorOrientation]: makeLabelGetter(t),
    // [CsticNames.LubricationConnectorPosition]: makeLabelGetter(t),
    [CsticNames.LubricationConnector]: makeLabelGetter(t),
    // [CsticNames.LubricationConnectorAlignment]: makeLabelGetter(t),
  };

  inputCsticNames.forEach((inputCsticName) => {
    const subkmats = summaryProvider.kmats[carriageDetailsStep.kmatType];
    const mainDef = getSubKmatCstics(carriageDetailsStep, 0).input[inputCsticName];
    const getValue = inputCsticNamesFormatting[inputCsticName];
    const firstSubkmat = subkmats[0];
    const nameKey = firstSubkmat.cstics[mainDef.name].label;

    const entry: CarriageKmatSummaryData = {
      name: t(nameKey),
      rail: [],
    };
    for (let railIndex = 0; railIndex < railAmount; railIndex++) {
      const rail: CarriageKmatSummaryData["rail"][0] = {
        carriage: [],
      };
      for (let carriageIndex = 0; carriageIndex < carriagesPerRail; carriageIndex++) {
        const flatCarriageIndex = railIndex * carriagesPerRail + carriageIndex;
        const subkmat = subkmats[flatCarriageIndex];
        const def = getSubKmatCstics(carriageDetailsStep, flatCarriageIndex).input[inputCsticName];
        const cstic = subkmat.cstics[def.name];
        rail.carriage.push({
          isStandard: getIsStandard(cstic),
          value: getValue(cstic),
        });
      }
      entry.rail.push(rail);
    }
    summaryData.push(entry);
  });

  summaryData.forEach((data) => {
    data.name = removeMultipleTags(data.name, "p", "span")!;
    data.rail.forEach((rail) => {
      rail.carriage.forEach((entry) => {
        entry.value = removeMultipleTags(entry.value, "p", "span")!;
      });
    });
  });

  return summaryData;
}

export function prepareSubStepsInfo(summaryProvider: SummaryDataProvider, substeps: SubstepType[]) {
  const substepData = substeps
    .map((substepType) => [substepType, summaryProvider.prepareTileSubstepSummary(substepType)] as const)
    .filter(([, summary]) => isNonNull(summary)) as [SubstepType, SummaryData][];

  if (substepData.length === 0) {
    return null;
  }

  substepData.forEach((data) => {
    data[1].value = removeMultipleTags(data[1].value, "p", "span")!;
    data[1].name = removeMultipleTags(data[1].name, "p", "span")!;
  });

  const data: Record<string, SummaryData> = toMapFn(
    substepData,
    ([type]) => type,
    ([, summary]) => summary,
  );

  return substeps.map((stepName) => data[stepName]).filter(Boolean) as SummaryData[];
}

function formatNumberValue(value: string | undefined, unit?: string) {
  let val = formatCsticValueAsNumber(value, 2);
  if (unit) {
    val += unit;
  }
  return val;
}

function getIsStandard(cstic: LinearCstic) {
  const cValue = cstic.values.find((cVal) => cVal.value === cstic.value);
  return cValue?.standard ?? false;
}
